import useUser from "components/common/useUser";
import Icon from "components/Icons/Icon";
import { useAppDownloadUrl } from "components/common/useAppStoreLink";
import trackEvent from "components/tracking/trackEvent";
import trackFathom from "components/tracking/trackFathom";
import { PACKING_LIST_CONTENT } from "components/website/marketing/content/featurePageContent";
import { PlanGateModal, usePlanGate } from "components/website/components/planGate";
import { useBusinessContext, useOrganisation } from "context/BusinessContext";
import React, { useState } from "react";
import { OsTypes, isMobile, osName } from "react-device-detect";
import { useAuthState } from "react-firebase-hooks/auth";
import useReactRouter from "use-react-router";
import { t } from "i18n/index";
import { localizeHref, MarketingLangCode } from "i18n/urlLocale";
import firebase from "../../firebase";
import FAQSection from "../components/FAQSection";
import DownloadAppModal from "../components/DownloadAppModal";
import DownloadAppQrCard from "../components/DownloadAppQrCard";
import Footer from "../components/Footer";
import HowItWorksSection from "../components/HowItWorksSection";
import Navigation from "../components/navigation/Navigation";
import LanguageSwitcher from "../components/LanguageSwitcher";
import "../css/PackingList.scss";
import "../css/Website.scss";
import TemplatesVisual from "../img/screenshots/packinglist/Packing1.png";
import SharedListVisual from "../img/screenshots/packinglist/Packing4.png";
import AssignItemsVisual from "../img/screenshots/packinglist/Packing3.png";
import CustomListVisual from "../img/screenshots/packinglist/Packing2.png";
import { getPackingListTranslations } from "../marketing/i18n/pages/packingList";

const S_PATH =
	"M17.3912 99.1864C14.9217 99.1864 12.9613 101.159 13.0092 103.593C13.0589 106.025 15.0991 108 17.5668 108C20.0363 108 21.9967 106.025 21.9488 103.593C21.8991 101.159 19.8589 99.1864 17.3912 99.1864ZM65.3336 33.3614C76.1235 30.4927 80.4221 24.1043 80.431 16.7419C80.4434 6.75566 72.3519 0 59.0871 0C38.1939 0 0 22.4846 0 43.1187C0 59.3036 21.0387 64.5548 35.3004 67.8972C49.5639 71.2377 60.588 74.2732 60.588 78.2187C60.588 82.1625 48.0081 91.0523 27.7464 97.4815C25.575 98.2036 24.3065 100.144 24.9097 101.816C25.5147 103.487 27.7145 104.079 29.9356 103.533C38.3749 101.452 78.201 92.3474 78.201 74.4204C78.201 56.4935 35.2862 49.1612 35.2862 45.9005C35.2862 42.5528 56.6017 35.6819 65.3336 33.3614Z";

interface PackingListPageProps {
	lang?: string;
}

export default function PackingListPage({ lang }: PackingListPageProps) {
	const [user] = useAuthState(firebase.auth);
	const { user: stipplUser, initializing } = useUser();
	const { history, match } = useReactRouter();
	const [showDownloadModal, setShowDownloadModal] = useState(false);
	const { isBusinessMode: isBusiness } = useBusinessContext();
	const { organisation, loading: orgLoading } = useOrganisation();
	const translations = getPackingListTranslations(lang || "en");
	// 123yb691qz1: every /register anchor on this page opens the signup modal in place instead
	// of navigating; `mobile: "store"` keeps 123yb691bdn's rule that a phone goes to the store
	// instead. `match.path` reuses the label `handleTrack("signup")` already tracked under.
	const gate = usePlanGate(match.path, "store");

	React.useEffect(() => {
		if (!user || !stipplUser?.createdAt) return;
		if (isBusiness) {
			if (orgLoading) return;
			const orgName = organisation?.name;
			if (orgName) history.push(`/${encodeURIComponent(orgName)}`);
			return;
		}
		const hasValidUsername = !!stipplUser?.username && !stipplUser?.username?.includes("StipplUser");
		if (hasValidUsername) {
			history.push("/home");
		} else {
			history.push("/onboarding");
		}
	}, [user, initializing, stipplUser, isBusiness, organisation, orgLoading]);

	const handleTrack = (type: "signup" | "download" | "appstore" | "googleplay") => {
		if (type === "appstore") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else if (type === "googleplay") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.Android && type === "download") {
			trackFathom("BNQKH0MH");
			trackEvent("click_googleplay_cta", { path: match.path }, "marketing_site");
		} else if (osName === OsTypes.IOS && type === "download") {
			trackFathom("FD21GVOP");
			trackEvent("click_appstore_cta", { path: match.path }, "marketing_site");
		} else {
			trackEvent("click_signup_cta", { path: match.path }, "marketing_site");
			trackFathom("E4NT4HWN");
		}
	};

	// 123yb6937h3: both "Download the app" CTAs below resolve their href through this -- always a
	// real, attributed OneLink, including on the very first render before mount (see
	// useAppDownloadUrl's doc comment in useAppStoreLink.ts). Previously these were <button
	// onClick={setShowDownloadModal(true)}> with no href at all, so the server HTML had a dead
	// control -- confirmed on production by QA's curl retest of 123yb6937h3.
	const appDownloadUrl = useAppDownloadUrl();

	return (
		<div className="packing-list" id="stippl-packing-list">
			<Navigation impact={true} langMenu={<LanguageSwitcher type="page" />} />

			{/* HERO */}
			<section className="hr-hero">
				<svg className="hero-s-mark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<svg className="hero-s-mark-left" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div className="container__inner hr-hero__inner">
					<div className="hr-hero__content">
						<div className="hero-chip">{t(translations, "packingList.hero.chip", "Packing list")}</div>
						<h1>
							{t(translations, "packingList.hero.title1", "Never forget a thing.")}
							<br />
							<span className="hero__accent">{t(translations, "packingList.hero.titleAccent", "Pack any trip in minutes.")}</span>
						</h1>
						<p className="hr-hero__desc">
							{t(translations, "packingList.hero.descPre", "Stippl's built-in packing list gives you pre-made templates for any trip type, shared")}{" "}
							<a href="#group-checklist" style={{ color: "var(--main2)", textDecoration: "underline" }}>
								{t(translations, "packingList.hero.descGroupChecklists", "group checklists")}
							</a>{" "}
							{t(translations, "packingList.hero.descMid", "and item assignment, so nothing gets forgotten and nothing gets packed twice. All alongside your")}{" "}
							<a href={localizeHref("/itinerary-planner", lang as MarketingLangCode)} style={{ color: "var(--main2)", textDecoration: "underline" }}>
								{t(translations, "packingList.hero.descItinerary", "itinerary")}
							</a>{" "}
							{t(translations, "packingList.hero.descAnd", "and")}{" "}
							<a href={localizeHref("/budget-planner", lang as MarketingLangCode)} style={{ color: "var(--main2)", textDecoration: "underline" }}>
								{t(translations, "packingList.hero.descBudget", "budget")}
							</a>
							{t(translations, "packingList.hero.descEnd", ".")}
						</p>
						<div className="hero-ctas" style={{ flexDirection: "column", alignItems: "flex-start", gap: "20px" }}>
							<div style={{ display: "flex", gap: "12px", flexWrap: "wrap", alignItems: "center" }}>
								{/* 123yb6937h3: on a phone the anchor is simply followed -- OneLink does the OS split
									(App Store vs Play Store) server-side, which is the ticket's acceptance criterion.
									On desktop we still want DownloadAppModal's QR code as the affordance, so the click
									handler -- browser-only, unlike a render-time device check -- prevents the default
									navigation and opens the modal instead. */}
								<a
									href={appDownloadUrl}
									className="hero-glass-btn"
									onClick={(e) => {
										handleTrack("download");
										if (!isMobile) {
											e.preventDefault();
											setShowDownloadModal(true);
										}
									}}
								>
									<div className="hero-glass-btn__icon">
										<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
											<path d={S_PATH} fill="#fff" />
										</svg>
									</div>
									<span className="hero-glass-btn__label">{t(translations, "packingList.hero.downloadApp", "Download the app")}</span>
								</a>
								<a
									href="/register"
									className="hero-web-link"
									onClick={(e) => {
										e.preventDefault();
										gate.requestPlan();
									}}
								>
									{t(translations, "packingList.hero.startOnWeb", "Start on web →")}
								</a>
							</div>
						</div>
					</div>
					<img
						className="hr-hero__phone hr-hero__phone--front"
						src={TemplatesVisual}
						alt={t(translations, "packingList.hero.phoneAlt", "Packing list screenshot")}
						loading="eager"
					/>
				</div>
			</section>

			{/* FEATURES GRID */}
			<section>
				<div className="section-wrapper" style={{ textAlign: "center" }}>
					<div className="section-chip chip-pink" style={{ margin: "0 auto 16px" }}>
						{t(translations, "packingList.features.chip", "Everything in one place")}
					</div>
					<h2>
						{t(translations, "packingList.features.title1", "Your trip packing list,")}
						<br />
						<span>{t(translations, "packingList.features.titleAccent", "built into your itinerary")}</span>
					</h2>
					<p className="body-text" style={{ maxWidth: "560px", margin: "0 auto" }}>
						{t(
							translations,
							"packingList.features.desc",
							"Stippl's travel packing list is built directly into your trip: part of how you plan, not a separate tool. Your packing list lives alongside your itinerary and budget, so nothing slips through the cracks."
						)}
					</p>
					<div className="features-small-grid">
						<div className="feature-small-card">
							<div className="feature-small-icon" style={{ background: "var(--main2light)" }}>
								<Icon name="list" size={20} pathProps={{ stroke: "var(--main2)" }} />
							</div>
							<div className="feature-small-title">{t(translations, "packingList.features.card1Title", "Pre-built templates")}</div>
							<div className="feature-small-text">
								{t(
									translations,
									"packingList.features.card1Text",
									"Start from a pre-made packing list template for your trip type: beach, hiking, city break, business and more."
								)}
							</div>
						</div>
						<div className="feature-small-card">
							<div className="feature-small-icon" style={{ background: "var(--main1light)" }}>
								<Icon name="peoples" size={20} pathProps={{ stroke: "var(--main1)" }} />
							</div>
							<div className="feature-small-title">{t(translations, "packingList.features.card2Title", "Shared packing lists")}</div>
							<div className="feature-small-text">
								{t(
									translations,
									"packingList.features.card2Text",
									"Create a shared packing list your whole group can see, check off and add to: in real time, from any device."
								)}
							</div>
						</div>
						<div className="feature-small-card">
							<div className="feature-small-icon" style={{ background: "var(--main4light)" }}>
								<Icon name="tag" size={20} fill="var(--main4)" />
							</div>
							<div className="feature-small-title">{t(translations, "packingList.features.card3Title", "Assign items to people")}</div>
							<div className="feature-small-text">
								{t(
									translations,
									"packingList.features.card3Text",
									"Assign specific items to specific people so everyone knows what they're responsible for bringing."
								)}
							</div>
						</div>
						<div className="feature-small-card">
							<div className="feature-small-icon" style={{ background: "var(--main3light)" }}>
								<Icon name="edit-pencil" size={20} stroke="var(--main3)" />
							</div>
							<div className="feature-small-title">{t(translations, "packingList.features.card4Title", "Fully customisable")}</div>
							<div className="feature-small-text">
								{t(
									translations,
									"packingList.features.card4Text",
									"Build your own custom packing list from scratch or modify any template to fit exactly what your trip needs."
								)}
							</div>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: PRE-BUILT TEMPLATES */}
			<section className="section-alt">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-pink">{t(translations, "packingList.templates.chip", "Pre-built templates")}</div>
							<h2>
								{t(translations, "packingList.templates.title1", "The easiest way to")}
								<br />
								<span>{t(translations, "packingList.templates.titleAccent", "start your packing list")}</span>
							</h2>
							<p className="body-text">
								{t(
									translations,
									"packingList.templates.desc",
									"Stippl comes with pre-built packing list templates organised by trip type. Pick the template that matches your trip, remove what doesn't apply, and add anything specific to your trip."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon pink">
										<Icon name="beach" size={18} fill="var(--main2)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.templates.item1Title", "Templates for every trip type")}</h4>
										<p>
											{t(
												translations,
												"packingList.templates.item1Text",
												"Beach holiday, city break, hiking, business travel, backpacking and more, all pre-built."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon pink">
										<Icon name="refresh-cycle" size={18} pathProps={{ fill: "var(--main2)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.templates.item2Title", "Reuse for future trips")}</h4>
										<p>
											{t(
												translations,
												"packingList.templates.item2Text",
												"Your packing lists are saved inside your trips and can be copied and adapted for next time."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon pink">
										<Icon name="edit-pencil" size={18} stroke="var(--main2)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.templates.item3Title", "Fully editable")}</h4>
										<p>
											{t(
												translations,
												"packingList.templates.item3Text",
												"Add, remove or rename any item. The template is just a starting point, make it yours."
											)}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "packingList.templates.cta", "Start from a packing list template →")}
							</a>
						</div>
						<div className="feature-visual">
							<img
								src={TemplatesVisual}
								alt={t(translations, "packingList.templates.imgAlt", "Packing list templates screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: SHARED PACKING LISTS */}
			<section id="group-checklist">
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-copy">
							<div className="section-chip chip-pink">{t(translations, "packingList.shared.chip", "Shared packing lists")}</div>
							<h2>
								{t(translations, "packingList.shared.title1", "Shared packing list")}
								<br />
								<span>{t(translations, "packingList.shared.titleAccent", "for your whole group.")}</span>
							</h2>
							<p className="body-text">
								{t(translations, "packingList.shared.descPre", "Stippl is a free shared packing list app built for")}{" "}
								<span>{t(translations, "packingList.shared.descGroupTravel", "group travel")}</span>
								{t(
									translations,
									"packingList.shared.descPost",
									". Create a group packing list, invite your travel partners, and everyone can see items, check things off and add their own, in real time from any device."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="eye" size={18} pathProps={{ stroke: "var(--main1)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.shared.item1Title", "Visible to everyone")}</h4>
										<p>{t(translations, "packingList.shared.item1Text", "Shared lists are live: when someone checks an item, everyone sees it instantly.")}</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="user" size={18} fill="var(--main1)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.shared.item2Title", "Individual lists too")}</h4>
										<p>
											{t(
												translations,
												"packingList.shared.item2Text",
												"Create personal checklists for items only you need to pack, private to each traveller."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon green">
										<Icon name="phone" size={18} fill="var(--main1)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.shared.item3Title", "Syncs across all devices")}</h4>
										<p>{t(translations, "packingList.shared.item3Text", "Plan on web, check off on mobile. Everything stays in sync as you pack.")}</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								style={{ color: "var(--main2)" }}
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "packingList.shared.cta", "Create a shared packing list →")}
							</a>
						</div>
						<div className="feature-visual">
							<img src={SharedListVisual} alt={t(translations, "packingList.shared.imgAlt", "Shared packing list screenshot")} className="feature-screenshot" />
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: ASSIGN ITEMS */}
			<section className="section-alt">
				<div className="section-wrapper">
					<div className="feature-split reverse">
						<div className="feature-copy">
							<div className="section-chip chip-pink">{t(translations, "packingList.assign.chip", "Assign items")}</div>
							<h2>
								{t(translations, "packingList.assign.title1", "Everyone knows")}
								<br />
								<span>{t(translations, "packingList.assign.titleAccent", "what they're bringing.")}</span>
							</h2>
							<p className="body-text">
								{t(
									translations,
									"packingList.assign.desc",
									"The classic group travel problem: everyone packs the same things and no one packs the essentials. Stippl's trip packing list lets you assign any item to a specific person so responsibilities are clear from the start."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="target" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.assign.item1Title", "Assign to any traveller")}</h4>
										<p>
											{t(
												translations,
												"packingList.assign.item1Text",
												"Pick who's responsible for each item: tent, first aid kit, travel router, wine opener."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="bell" size={18} fill="var(--main4)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.assign.item2Title", "No more excuses")}</h4>
										<p>
											{t(translations, "packingList.assign.item2Text", "When items are assigned, everyone can see exactly who was supposed to bring what.")}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon purple">
										<Icon name="lightning" size={18} pathProps={{ stroke: "var(--main4)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.assign.item3Title", "Set quantities")}</h4>
										<p>
											{t(
												translations,
												"packingList.assign.item3Text",
												"Log how many of each item you need, useful for group supplies like sunscreen or snacks."
											)}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								style={{ color: "var(--main2)" }}
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "packingList.assign.cta", "Assign items to your travel group →")}
							</a>
						</div>
						<div className="feature-visual">
							<img
								src={AssignItemsVisual}
								alt={t(translations, "packingList.assign.imgAlt", "Assign packing items screenshot")}
								className="feature-screenshot"
							/>
						</div>
					</div>
				</div>
			</section>

			{/* FEATURE: CUSTOM LISTS */}
			<section>
				<div className="section-wrapper">
					<div className="feature-split">
						<div className="feature-copy">
							<div className="section-chip chip-pink">{t(translations, "packingList.custom.chip", "Custom lists & mobile")}</div>
							<h2>
								{t(translations, "packingList.custom.title1", "Custom packing list.")}
								<br />
								<span>{t(translations, "packingList.custom.titleAccent", "Your way.")}</span>
							</h2>
							<p className="body-text">
								{t(
									translations,
									"packingList.custom.desc",
									"Every trip is different, and your travel packing list should be too. Create fully custom checklists, save them and reuse on any future trip. Works completely offline so you can check items off as you pack, no Wi-Fi needed."
								)}
							</p>
							<div className="feature-list">
								<div className="feature-list-item">
									<div className="feature-list-icon amber">
										<Icon name="edit-pencil" size={18} stroke="var(--main3)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.custom.item1Title", "Build from scratch")}</h4>
										<p>
											{t(
												translations,
												"packingList.custom.item1Text",
												"Create a custom packing list with your own categories, items and quantities, no template required."
											)}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon amber">
										<Icon name="refresh-cycle" size={18} pathProps={{ fill: "var(--main3)" }} />
									</div>
									<div className="feature-list-copy">
										<h4>
											{t(translations, "packingList.custom.item2Title", "Save and reuse")} <span className="pro-badge">PRO</span>
										</h4>
										<p>
											{t(translations, "packingList.custom.item2Text", "Build your perfect packing list once and apply it to every future trip in one click.")}
										</p>
									</div>
								</div>
								<div className="feature-list-item">
									<div className="feature-list-icon amber">
										<Icon name="phone" size={18} fill="var(--main3)" />
									</div>
									<div className="feature-list-copy">
										<h4>{t(translations, "packingList.custom.item3Title", "Works offline")}</h4>
										<p>
											{t(
												translations,
												"packingList.custom.item3Text",
												"Check items off as you pack, no Wi-Fi needed. Everything syncs automatically when you're back online."
											)}
										</p>
									</div>
								</div>
							</div>
							<a
								href="/register"
								className="feature-cta-link"
								style={{ color: "var(--main2)" }}
								onClick={(e) => {
									e.preventDefault();
									gate.requestPlan();
								}}
							>
								{t(translations, "packingList.custom.cta", "Build your packing list →")}
							</a>
						</div>
						<div className="feature-visual">
							<img src={CustomListVisual} alt={t(translations, "packingList.custom.imgAlt", "Custom packing list screenshot")} className="feature-screenshot" />
						</div>
					</div>
				</div>
			</section>

			{/* HOW IT WORKS */}
			<HowItWorksSection
				theme="pink"
				tinted
				title={t(translations, "packingList.howToUse.name", PACKING_LIST_CONTENT.howToUse.name)}
				description={t(translations, "packingList.howToUse.description", PACKING_LIST_CONTENT.howToUse.description)}
				steps={PACKING_LIST_CONTENT.howToSteps.map((step, index) => ({
					...step,
					name: t(translations, `packingList.howToSteps.${index}.name`, step.name),
					text: t(translations, `packingList.howToSteps.${index}.text`, step.text),
				}))}
				ctaText={t(translations, "packingList.howItWorks.cta", "Start your packing list for free →")}
				onCtaClick={() => handleTrack("signup")}
			/>

			{/* PRO CALLOUT */}
			<section className="pro-callout-section">
				<div className="section-wrapper">
					<div className="pro-callout-block">
						<div className="pro-callout-left">
							<div className="pro-callout-label">Stippl Pro</div>
							<h3>{t(translations, "packingList.pro.title", "Pack smarter on every trip")}</h3>
							<p>
								{t(
									translations,
									"packingList.pro.desc",
									"The packing list is free. Pro lets you save your lists once and reuse them on every future trip, no starting from scratch."
								)}
							</p>
							<div className="pro-features-list">
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "packingList.pro.feature1Strong", "Reusable packing checklists")}</strong>{" "}
										{t(
											translations,
											"packingList.pro.feature1Text",
											", save your perfect list once and apply it to every new trip instantly. Weekend bag? Beach trip? Winter ski? One click."
										)}
									</span>
								</div>
								<div className="pro-feature-row">
									<div className="pro-feature-check">
										<svg width="12" height="12" viewBox="0 0 12 12" fill="none">
											<polyline points="2,6 5,9 10,3" stroke="#fff" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
										</svg>
									</div>
									<span>
										<strong>{t(translations, "packingList.pro.feature2Strong", "Packing list templates across trip types")}</strong>{" "}
										{t(
											translations,
											"packingList.pro.feature2Text",
											": Pro unlocks all template categories including backpacking, winter sports and extended travel."
										)}
									</span>
								</div>
							</div>
						</div>
						<div className="pro-price-card">
							<svg className="pro-price-card-smark" viewBox="0 0 81 108" fill="none" xmlns="http://www.w3.org/2000/svg">
								<path d={S_PATH} fill="white" />
							</svg>
							<div className="pro-price-label">{t(translations, "packingList.pro.priceLabel", "Starting from")}</div>
							<div className="pro-price-amount">€3</div>
							<div className="pro-price-period">{t(translations, "packingList.pro.pricePeriod", "per month")}</div>
							<a href={localizeHref("/pro", lang as MarketingLangCode)} className="pro-price-btn">
								<svg width="14" height="18" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="currentColor" />
								</svg>
								{t(translations, "packingList.pro.priceBtn", "Get Pro →")}
							</a>
							<div className="pro-price-note">{t(translations, "packingList.pro.priceNote", "Cancel anytime · No commitment")}</div>
						</div>
					</div>
				</div>
			</section>

			{/* FAQ */}
			<section>
				<div className="section-wrapper">
					<FAQSection
						heading={t(translations, "packingList.faq.heading", "Frequently asked questions about our travel packing list app")}
						items={PACKING_LIST_CONTENT.faqItems.map((item, index) => ({
							...item,
							question: t(translations, `packingList.faqItems.${index}.question`, item.question),
							answer: t(translations, `packingList.faqItems.${index}.answer`, item.answer),
						}))}
					/>
				</div>
			</section>

			{/* CTA STRIP */}
			<section className="cta-strip">
				<svg className="cta-s-mark" viewBox="0 0 81 108" fill="none">
					<path d={S_PATH} fill="#ffffff" />
				</svg>
				<div style={{ position: "relative", zIndex: 2 }}>
					<h2 style={{ color: "#fff" }}>
						{t(translations, "packingList.ctaStrip.title1", "Never forget a thing")}
						<br />
						{t(translations, "packingList.ctaStrip.title2", "on your next trip.")}
					</h2>
					<p>{t(translations, "packingList.ctaStrip.desc", "Free for 1.5 million+ travellers. Packing list, itinerary and budget, all in one place.")}</p>
					<div className="cta-strip-btns">
						{/* Same fix as the hero CTA above (123yb6937h3): a real href on the server render, store
							link on mobile, modal-with-QR on desktop. */}
						<a
							href={appDownloadUrl}
							className="hero-glass-btn"
							onClick={(e) => {
								handleTrack("download");
								if (!isMobile) {
									e.preventDefault();
									setShowDownloadModal(true);
								}
							}}
						>
							<div className="hero-glass-btn__icon">
								<svg width="16" height="21" viewBox="0 0 81 108" fill="none">
									<path d={S_PATH} fill="#fff" />
								</svg>
							</div>
							<span className="hero-glass-btn__label">{t(translations, "packingList.hero.downloadApp", "Download the app")}</span>
						</a>
						<a
							href="/register"
							className="hero-web-link"
							onClick={(e) => {
								e.preventDefault();
								gate.requestPlan();
							}}
						>
							{t(translations, "packingList.hero.startOnWeb", "Start on web →")}
						</a>
					</div>
				</div>
			</section>

			<Footer />
			<DownloadAppQrCard />
			<DownloadAppModal isOpen={showDownloadModal} onClose={() => setShowDownloadModal(false)} location={match.path} />
			<PlanGateModal gate={gate} title="Create a free account to start planning" />
		</div>
	);
}
